import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Eye, EyeOff, LifeBuoy, Loader2 } from 'lucide-react';
import { adminDashboard, adminListDraws, adminToggleDrawVisibility, api } from '@/lib/api';
import { formatGBP, formatGBPfromPence } from '@/lib/format';

const Kpi = ({ label, value, testid }) => (
  <div className="bg-surface border border-pd rounded-2xl p-5" data-testid={testid}>
    <div className="font-mono text-[10px] text-muted-sage uppercase">{label}</div>
    <div className="font-display text-4xl text-[color:var(--pd-accent-yellow)] mt-2">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const [d, setD] = useState(null);
  const [reseeding, setReseeding] = useState(false);
  const [purging, setPurging] = useState(false);
  const [reconcile, setReconcile] = useState({ orderRef: '', paymentRef: '', loading: false, lookup: null, lookupErr: null, result: null, probe: null });
  const [draws, setDraws] = useState(null);
  const [drawsBusy, setDrawsBusy] = useState(null); // id currently toggling
  useEffect(() => { adminDashboard().then(setD).catch(() => {}); }, []);
  useEffect(() => { adminListDraws().then((r) => setDraws(r.items || [])).catch(() => setDraws([])); }, []);

  const toggleDrawVisibility = async (draw) => {
    const nextHidden = !draw.hidden_from_public;
    setDrawsBusy(draw.id);
    try {
      await adminToggleDrawVisibility(draw.id, nextHidden);
      toast.success(nextHidden ? 'Draw hidden from public log' : 'Draw restored to public log');
      const r = await adminListDraws();
      setDraws(r.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update visibility');
    } finally {
      setDrawsBusy(null);
    }
  };

  const lookupOrder = async () => {
    const ref = reconcile.orderRef.trim();
    if (!ref) return;
    setReconcile((s) => ({ ...s, loading: true, lookup: null, lookupErr: null, result: null, probe: null }));
    try {
      const r = await api.get(`/admin/orders/${encodeURIComponent(ref)}`).then((res) => res.data);
      setReconcile((s) => ({ ...s, loading: false, lookup: r }));
    } catch (err) {
      setReconcile((s) => ({
        ...s,
        loading: false,
        lookup: null,
        lookupErr: err?.response?.data?.detail || 'Order not found',
      }));
    }
  };

  const runProbe = async () => {
    const ref = reconcile.orderRef.trim();
    if (!ref) return;
    setReconcile((s) => ({ ...s, loading: true, probe: null }));
    try {
      const body = reconcile.paymentRef.trim() ? { payment_ref: reconcile.paymentRef.trim() } : {};
      const r = await api.post(`/admin/orders/${encodeURIComponent(ref)}/probe`, body).then((res) => res.data);
      setReconcile((s) => ({ ...s, loading: false, probe: r }));
      if (r.probe_ok) {
        toast.info(`Cashflows says: ${r.raw_status || '\u2014'} \u2192 ${r.normalised_status}`);
      } else {
        toast.error(`Probe failed: ${r.error || 'unknown error'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Probe failed');
      setReconcile((s) => ({ ...s, loading: false }));
    }
  };

  const runReconcile = async () => {
    const ref = reconcile.orderRef.trim();
    if (!ref) return;
    setReconcile((s) => ({ ...s, loading: true, result: null }));
    try {
      const body = reconcile.paymentRef.trim() ? { payment_ref: reconcile.paymentRef.trim() } : {};
      const r = await api.post(`/admin/orders/${encodeURIComponent(ref)}/reconcile`, body).then((res) => res.data);
      const after = r.after || {};
      if (after.payment_status === 'paid' && after.tickets_created) {
        toast.success(`Order ${ref} reconciled \u2014 ${r.tickets_in_db} ticket${r.tickets_in_db === 1 ? '' : 's'} issued.`);
      } else if (after.payment_status === 'paid') {
        toast.info(`Order ${ref} is paid but tickets weren't created (likely refund_pending). Check details.`);
      } else {
        toast.info(`Order ${ref} status: ${after.payment_status || 'unknown'}. Cashflows may still be processing.`);
      }
      // Refresh the lookup panel.
      const fresh = await api.get(`/admin/orders/${encodeURIComponent(ref)}`).then((res) => res.data);
      setReconcile((s) => ({ ...s, loading: false, lookup: fresh, result: r }));
      adminDashboard().then(setD).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Reconcile failed';
      toast.error(msg);
      setReconcile((s) => ({ ...s, loading: false, result: null }));
    }
  };

  const runReseed = async () => {
    if (!window.confirm('Restore any missing launch competitions? This is safe to run — it never touches existing records, orders, or real customer entries.')) return;
    setReseeding(true);
    try {
      const r = await api.post('/admin/reseed').then((res) => res.data);
      if (r.restored?.length) {
        toast.success(`Restored ${r.restored.length} competition${r.restored.length === 1 ? '' : 's'}: ${r.restored.join(', ')}`);
      } else {
        toast.info('Nothing to restore — all 5 launch competitions are already present.');
      }
      adminDashboard().then(setD).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally { setReseeding(false); }
  };

  // One-time launch cleanup. Deletes:
  //   • entries with source == "seed"  (pre-launch demo tickets)
  //   • competitions with slug starting "test-comp-"
  //   • unpaid entries and abandoned/pending orders
  // Never touches real customer entries (cashflows/postal), paid orders,
  // users, winners, draws, waitlist or charity records.
  const runPurgeSeed = async () => {
    const confirmed = window.confirm(
      "Permanently delete pre-launch demo data?\n\n" +
      "This removes fake 'seed' tickets, empty checkout attempts, and any leftover 'test-comp-*' competitions from the database.\n\n" +
      "It NEVER touches real customer entries, paid orders, users, winners, draws, or the waitlist.\n\n" +
      "This action cannot be undone. Continue?"
    );
    if (!confirmed) return;
    setPurging(true);
    try {
      const r = await api.post('/admin/purge-seed-data', { confirm: 'PURGE' }).then((res) => res.data);
      const total = (r.seed_entries_deleted || 0) + (r.unpaid_entries_deleted || 0)
                  + (r.test_competitions_deleted || 0) + (r.abandoned_orders_deleted || 0);
      if (total === 0) {
        toast.info('Nothing to purge — database is already clean.');
      } else {
        toast.success(
          `Purged: ${r.seed_entries_deleted} seed tickets · ${r.unpaid_entries_deleted} unpaid entries · ` +
          `${r.test_competitions_deleted} test competitions · ${r.abandoned_orders_deleted} abandoned orders.`
        );
      }
      adminDashboard().then(setD).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally { setPurging(false); }
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] text-muted-sage uppercase">Overview</div>
          <h1 className="font-display text-4xl mt-2">Dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runReseed}
            disabled={reseeding}
            className="border border-[color:var(--pd-gold)] text-[color:var(--pd-gold)] rounded-lg px-3 py-2 text-sm hover:bg-[color:var(--pd-gold)]/10 disabled:opacity-60"
            title="Restore any missing launch competitions"
            data-testid="admin-reseed-button"
          >
            {reseeding ? 'Restoring…' : 'Restore launch competitions'}
          </button>
          <button
            onClick={runPurgeSeed}
            disabled={purging}
            className="inline-flex items-center gap-2 border border-[color:var(--pd-accent-orange)]/60 text-[color:var(--pd-accent-orange)] rounded-lg px-3 py-2 text-sm hover:bg-[color:var(--pd-accent-orange)]/10 disabled:opacity-60"
            title="One-time cleanup: delete pre-launch demo data (seed tickets, test competitions, abandoned checkouts). Real customer data is never touched."
            data-testid="admin-purge-seed-button"
          >
            <AlertTriangle size={14} />
            {purging ? 'Purging…' : 'Purge pre-launch demo data'}
          </button>
        </div>
      </div>
      {d && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Live competitions" value={d.live_competitions} testid="kpi-live" />
          <Kpi label="Total competitions" value={d.total_competitions} testid="kpi-total-comps" />
          <Kpi label="Paid entries" value={d.paid_entries} testid="kpi-paid-entries" />
          <Kpi label="Revenue (test)" value={formatGBP(d.revenue_gbp)} testid="kpi-revenue" />
          <Kpi label="Winners" value={d.winners} testid="kpi-winners" />
          <Kpi label="Orders" value={d.orders} testid="kpi-orders" />
          <Kpi label="Postal pending" value={d.postal_pending} testid="kpi-postal" />
          <Kpi label="Waitlist" value={d.waitlist_count} testid="kpi-waitlist" />
        </div>
      )}

      {/* Reconcile Order: recover a paid Cashflows order that never issued
          tickets (webhook delayed or lost). Safe to run — idempotent. */}
      <div className="mt-8 bg-surface border border-pd rounded-2xl p-5" data-testid="admin-reconcile-card">
        <div className="flex items-start gap-3">
          <LifeBuoy size={18} className="text-[color:var(--pd-accent-yellow)] mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-mono text-[10px] text-muted-sage uppercase">Recovery tool</div>
            <h2 className="font-display text-2xl mt-1">Reconcile a paid order</h2>
            <p className="text-xs text-muted-sage mt-1">
              For a customer whose payment cleared but whose tickets never appeared (webhook was slow or lost). Pastes the order ref &mdash; and optionally the Cashflows payment ref &mdash; and we ask Cashflows directly, then issue tickets and send the confirmation email if it&rsquo;s paid. Idempotent, safe to run twice.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
          <div>
            <label className="block text-[11px] text-muted-sage font-mono uppercase mb-1">Order ref</label>
            <input
              type="text"
              placeholder="TPD-XXXXXXXX"
              value={reconcile.orderRef}
              onChange={(e) => setReconcile((s) => ({ ...s, orderRef: e.target.value, lookup: null, lookupErr: null, result: null }))}
              className="w-full bg-[color:var(--pd-bg)] border border-pd rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
              data-testid="admin-reconcile-order-ref"
            />
          </div>
          <div>
            <label className="block text-[11px] text-muted-sage font-mono uppercase mb-1">Cashflows payment ref (optional)</label>
            <input
              type="text"
              placeholder="From the /payment/success URL"
              value={reconcile.paymentRef}
              onChange={(e) => setReconcile((s) => ({ ...s, paymentRef: e.target.value }))}
              className="w-full bg-[color:var(--pd-bg)] border border-pd rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
              data-testid="admin-reconcile-payment-ref"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={lookupOrder}
              disabled={reconcile.loading || !reconcile.orderRef.trim()}
              className="border border-pd text-[color:var(--pd-text)] rounded-lg px-3 py-2 text-sm hover:bg-[color:var(--pd-bg)] disabled:opacity-50"
              data-testid="admin-reconcile-lookup"
            >
              Look up
            </button>
            <button
              onClick={runProbe}
              disabled={reconcile.loading || !reconcile.orderRef.trim()}
              className="border border-[color:var(--pd-gold)] text-[color:var(--pd-gold)] rounded-lg px-3 py-2 text-sm hover:bg-[color:var(--pd-gold)]/10 disabled:opacity-50"
              title="Ask Cashflows what they think — doesn't modify the order"
              data-testid="admin-reconcile-probe"
            >
              Probe Cashflows
            </button>
            <button
              onClick={runReconcile}
              disabled={reconcile.loading || !reconcile.orderRef.trim()}
              className="inline-flex items-center gap-2 bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-60"
              data-testid="admin-reconcile-run"
            >
              {reconcile.loading ? <Loader2 size={14} className="animate-spin" /> : null}
              Reconcile
            </button>
          </div>
        </div>

        {reconcile.lookupErr && (
          <div className="mt-3 text-xs text-[color:var(--pd-danger)]" data-testid="admin-reconcile-lookup-error">
            {reconcile.lookupErr}
          </div>
        )}

        {reconcile.lookup && (
          <div className="mt-4 rounded-xl border border-pd bg-[color:var(--pd-bg)] p-4 text-xs" data-testid="admin-reconcile-lookup-result">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <div><span className="text-muted-sage">Customer</span><div className="text-[color:var(--pd-text)]">{reconcile.lookup.full_name || '\u2014'} &middot; <span className="font-mono">{reconcile.lookup.email}</span></div></div>
              <div><span className="text-muted-sage">Competition</span><div className="text-[color:var(--pd-text)]">{reconcile.lookup.competition_title || '\u2014'}</div></div>
              <div><span className="text-muted-sage">Amount</span><div className="text-[color:var(--pd-text)] font-mono">{formatGBPfromPence(reconcile.lookup.amount_total)} for {reconcile.lookup.quantity} ticket{reconcile.lookup.quantity === 1 ? '' : 's'}</div></div>
              <div><span className="text-muted-sage">Status</span><div className="text-[color:var(--pd-text)]"><span className="font-mono">{reconcile.lookup.payment_status || 'unknown'}</span> &middot; tickets_created: <span className="font-mono">{String(reconcile.lookup.tickets_created)}</span></div></div>
              <div className="sm:col-span-2"><span className="text-muted-sage">Cashflows refs</span><div className="text-[color:var(--pd-text)] font-mono break-all">job: {reconcile.lookup.cashflows_payment_job_reference || '\u2014'} &middot; payment: {reconcile.lookup.cashflows_payment_reference || '\u2014'}</div></div>
              {reconcile.lookup.tickets?.length ? (
                <div className="sm:col-span-2"><span className="text-muted-sage">Tickets in DB ({reconcile.lookup.tickets.length})</span><div className="text-[color:var(--pd-text)] font-mono break-all">{reconcile.lookup.tickets.join(', ')}</div></div>
              ) : null}
              {reconcile.lookup.email_events?.length ? (
                <div className="sm:col-span-2">
                  <span className="text-muted-sage">Email history ({reconcile.lookup.email_events.length})</span>
                  <div className="mt-1 space-y-1">
                    {reconcile.lookup.email_events.map((e, i) => (
                      <div key={i} className="text-[color:var(--pd-text)] font-mono text-[11px] flex items-center gap-2">
                        <span className={e.sent ? 'text-[color:var(--pd-accent-yellow)]' : 'text-[color:var(--pd-danger)]'}>
                          {e.sent ? '\u2713 sent' : '\u2717 not sent'}
                        </span>
                        <span>{e.kind}</span>
                        <span className="text-muted-sage">{e.sent_at || e.created_at}</span>
                        {e.error ? <span className="text-[color:var(--pd-danger)] break-all">err: {e.error}</span> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="sm:col-span-2"><span className="text-muted-sage">Email history</span><div className="text-muted-sage italic">No email attempts recorded</div></div>
              )}
            </div>
          </div>
        )}

        {reconcile.probe && (
          <div className="mt-4 rounded-xl border border-[color:var(--pd-gold)]/50 bg-[color:var(--pd-bg)] p-4 text-xs" data-testid="admin-reconcile-probe-result">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">Cashflows probe</span>
              {reconcile.probe.probe_ok ? (
                <span className="text-[color:var(--pd-text)]">Raw: <span className="font-mono">{reconcile.probe.raw_status || '\u2014'}</span> &middot; Normalised: <span className="font-mono">{reconcile.probe.normalised_status}</span></span>
              ) : (
                <span className="text-[color:var(--pd-danger)]">Failed: {reconcile.probe.error}</span>
              )}
            </div>
            {reconcile.probe.raw_response ? (
              <div>
                <div className="text-muted-sage mb-1">Full response body (for support / forwarding to Cashflows):</div>
                <pre className="bg-[color:var(--pd-bg-2)] p-3 rounded-lg overflow-auto max-h-[280px] text-[11px] font-mono text-[color:var(--pd-text)] whitespace-pre-wrap break-all">
{JSON.stringify(reconcile.probe.raw_response, null, 2)}
                </pre>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Draws visibility: hide demo / test / redraw-superseded draws from the
          public /past-draws + /winners pages without deleting the audit record. */}
      <div className="mt-8 bg-surface border border-pd rounded-2xl p-5" data-testid="admin-draws-card">
        <div className="flex items-start gap-3">
          <Eye size={18} className="text-[color:var(--pd-accent-yellow)] mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-mono text-[10px] text-muted-sage uppercase">Draws visibility</div>
            <h2 className="font-display text-2xl mt-1">Public draws log</h2>
            <p className="text-xs text-muted-sage mt-1">
              Every draw that ever ran, including demo/test ones. Hide the demos so they never show on the public <span className="font-mono">/past-draws</span> and <span className="font-mono">/winners</span> pages &mdash; the audit record stays in the database for your own records.
            </p>
          </div>
        </div>

        <div className="mt-4">
          {draws === null ? (
            <div className="text-xs text-muted-sage flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading draws\u2026</div>
          ) : draws.length === 0 ? (
            <div className="text-xs text-muted-sage italic">No draws recorded yet.</div>
          ) : (
            <div className="rounded-xl border border-pd overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[color:var(--pd-bg)] text-muted-sage uppercase text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono">Competition</th>
                    <th className="text-left px-3 py-2 font-mono">Ticket</th>
                    <th className="text-left px-3 py-2 font-mono">Drawn</th>
                    <th className="text-left px-3 py-2 font-mono">Winner status</th>
                    <th className="text-left px-3 py-2 font-mono">Public?</th>
                    <th className="text-right px-3 py-2 font-mono">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--pd-border)]">
                  {draws.map((d) => (
                    <tr key={d.id} className="text-[color:var(--pd-text)]" data-testid={`admin-draw-row-${d.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{d.competition_title || '\u2014'}</div>
                        <div className="text-[10px] text-muted-sage font-mono">{d.competition_slug || d.competition_id}</div>
                      </td>
                      <td className="px-3 py-2 font-mono">{d.winner_ticket_number || '\u2014'}</td>
                      <td className="px-3 py-2 text-muted-sage">{d.drawn_at ? new Date(d.drawn_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '\u2014'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-mono uppercase ${d.winner_status === 'confirmed' ? 'bg-[color:var(--pd-accent-yellow)]/10 text-[color:var(--pd-accent-yellow)]' : d.winner_status === 'pending' ? 'bg-[color:var(--pd-gold)]/10 text-[color:var(--pd-gold)]' : 'bg-white/5 text-muted-sage'}`}>{d.winner_status || 'unknown'}</span>
                      </td>
                      <td className="px-3 py-2">
                        {d.hidden_from_public ? (
                          <span className="inline-flex items-center gap-1 text-muted-sage text-[10px]"><EyeOff size={12} /> Hidden</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[color:var(--pd-accent-yellow)] text-[10px]"><Eye size={12} /> Public</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => toggleDrawVisibility(d)}
                          disabled={drawsBusy === d.id}
                          className="border border-pd rounded-md px-2 py-1 text-[11px] hover:bg-[color:var(--pd-bg)] disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`admin-draw-toggle-${d.id}`}
                        >
                          {drawsBusy === d.id ? <Loader2 size={11} className="animate-spin" /> : (d.hidden_from_public ? <Eye size={11} /> : <EyeOff size={11} />)}
                          {d.hidden_from_public ? 'Restore' : 'Hide'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default AdminDashboard;
